import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { OPPORTUNITIES_QUERY_KEYS } from '@/entities/opportunity';

import type { SocialOpportunitiesWidget } from '@inference.engine/widgets/web';

import { logger } from '@/lib/utils/logger';

import type { SocialMediaOpportunitiesApiDeps } from '../../social-media-opportunities-widget.types';
import type { OpportunityItemWithFavorite } from '../../../ui/components/OpportunitiesList/OpportunitiesList.types';

interface UseOpportunitiesParams {
  data: SocialOpportunitiesWidget;
  workspaceId: string | null;
  /**
   * Opportunities api functions injected via the config-layer factory.
   * Wired at
   * `@/lib/config/agents.config.ts` from
   * `@/entities/opportunity`.
   *
   * OPTIONAL at the type level so legacy mounts (e.g. dashboard tests)
   * still compile; the hook fails fast if a query/mutation runs without
   * the deps.
   */
  api?: SocialMediaOpportunitiesApiDeps;
}

interface UseOpportunitiesReturn {
  opportunities: OpportunityItemWithFavorite[] | null;
  isLoading: boolean;
  favoritesOnly: boolean;
  setFavoritesOnly: (value: boolean) => void;
  handleToggleFavorite: (opportunityId: string, nextFavorite: boolean) => Promise<void>;
}

function requireApi(
  api: SocialMediaOpportunitiesApiDeps | undefined,
): SocialMediaOpportunitiesApiDeps {
  if (!api) {
    throw new Error(
      '[social-media-opportunities-widget] useOpportunities: `api` deps not injected. Wire via `opportunitiesDeps` at the config-layer factory (`@/lib/config/agents.config.ts`).',
    );
  }
  return api;
}

export function useOpportunities(params: UseOpportunitiesParams): UseOpportunitiesReturn {
  const { data, workspaceId, api } = params;
  const queryClient = useQueryClient();
  const [favoritesOnly, setFavoritesOnly] = useState<boolean>(false);

  const hasInitialItems = data.items.length > 0;
  const itemsKey = JSON.stringify(data.items.map((item) => item.opportunityId));

  useEffect(() => {
    if (hasInitialItems && workspaceId) {
      queryClient.setQueryData(OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId), data.items);
    }

    if (!hasInitialItems && workspaceId) {
      //trigger a refetch to populate data if there are no initial items
      void queryClient.invalidateQueries({
        queryKey: OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId),
      });
    }
    // `itemsKey` is a stable string-hash of `data.items` (same input,
    // narrower identity), and `hasInitialItems` is derived from
    // `data.items.length`. Including `data.items` directly would re-fire
    // the effect for every reference change even when the underlying
    // opportunity ids didn't move — `itemsKey` is the intentional
    // narrowing. eslint-react-hooks can't see this derivation chain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey, workspaceId, queryClient]);

  // Query for all opportunities
  const {
    data: allOpportunities,
    isLoading: isLoadingAll,
    isFetching: isFetchingAll,
  } = useQuery({
    // `api` is a stable reference resolved once at the config-layer factory;
    // not a value the user can change without re-mounting the agent. We
    // intentionally do not include it in the queryKey to keep cache keys
    // stable across mount paths.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId),
    queryFn: async () => {
      if (!workspaceId) throw new Error('Workspace ID required');
      const { getOpportunities } = requireApi(api);
      const response = await getOpportunities({
        workspaceId,
        take: 20,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      });
      return response.map(
        ({ data, isFavorite, durationType, ideationData, insightType }, index) =>
          ({
            ...data,
            isFavorite,
            durationType,
            ideationData,
            insightType,
            topRanked: index < 5, // Mark top 5 as topRanked
          }) as OpportunityItemWithFavorite,
      );
    },
    enabled: !!workspaceId && !favoritesOnly && !!api,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Query for favorite opportunities only
  const {
    data: favoriteOpportunities,
    isLoading: isLoadingFavorites,
    isFetching: isFetchingFavorites,
  } = useQuery({
    // `api` is a stable config-layer reference (see comment on the prior
    // query). Intentionally omitted from queryKey for cache stability.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: OPPORTUNITIES_QUERY_KEYS.favoritesByWorkspace(workspaceId),
    queryFn: async () => {
      if (!workspaceId) throw new Error('Workspace ID required');
      const { getOpportunities } = requireApi(api);
      const response = await getOpportunities({
        workspaceId,
        take: 10,
        favoritesOnly: true,
      });
      return response.map(
        ({ data, isFavorite, durationType, ideationData, insightType }) =>
          ({
            ...data,
            isFavorite,
            durationType,
            ideationData,
            insightType,
          }) as OpportunityItemWithFavorite,
      );
    },
    enabled: !!workspaceId && favoritesOnly && !!api,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Determine which data to show
  const opportunities = favoritesOnly ? favoriteOpportunities : allOpportunities;
  const isLoading = favoritesOnly
    ? isLoadingFavorites || isFetchingFavorites
    : isLoadingAll || isFetchingAll;

  // Mutation for toggling favorites
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({
      opportunityId,
      nextFavorite,
    }: {
      opportunityId: string;
      nextFavorite: boolean;
    }) => {
      if (!workspaceId) throw new Error('Workspace ID required');
      const { favoriteOpportunity, unfavoriteOpportunity } = requireApi(api);
      if (nextFavorite) {
        await favoriteOpportunity(workspaceId, opportunityId);
      } else {
        await unfavoriteOpportunity(workspaceId, opportunityId);
      }
    },
    onMutate: async ({ opportunityId, nextFavorite }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: OPPORTUNITIES_QUERY_KEYS.byWorkspace(workspaceId),
      });

      // Snapshot previous values
      const previousAll = queryClient.getQueryData<OpportunityItemWithFavorite[]>(
        OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId),
      );
      const previousFavorites = queryClient.getQueryData<OpportunityItemWithFavorite[]>(
        OPPORTUNITIES_QUERY_KEYS.favoritesByWorkspace(workspaceId),
      );

      // Optimistically update all opportunities
      if (previousAll) {
        queryClient.setQueryData<OpportunityItemWithFavorite[]>(
          OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId),
          previousAll.map((opp) =>
            opp.opportunityId === opportunityId ? { ...opp, isFavorite: nextFavorite } : opp,
          ),
        );
      }

      // Optimistically update favorites list
      if (previousFavorites) {
        if (nextFavorite) {
          // Find the opportunity from all list and add to favorites
          const oppToAdd = previousAll?.find((opp) => opp.opportunityId === opportunityId);
          if (oppToAdd) {
            queryClient.setQueryData<OpportunityItemWithFavorite[]>(
              OPPORTUNITIES_QUERY_KEYS.favoritesByWorkspace(workspaceId),
              [...previousFavorites, { ...oppToAdd, isFavorite: true }],
            );
          }
        } else {
          // Remove from favorites
          queryClient.setQueryData<OpportunityItemWithFavorite[]>(
            OPPORTUNITIES_QUERY_KEYS.favoritesByWorkspace(workspaceId),
            previousFavorites.filter((opp) => opp.opportunityId !== opportunityId),
          );
        }
      }

      return { previousAll, previousFavorites };
    },
    onError: (error, _variables, context) => {
      logger.error('Failed to toggle favorite:', error);
      // Rollback on error
      if (context?.previousAll) {
        queryClient.setQueryData(
          OPPORTUNITIES_QUERY_KEYS.allByWorkspace(workspaceId),
          context.previousAll,
        );
      }
      if (context?.previousFavorites) {
        queryClient.setQueryData(
          OPPORTUNITIES_QUERY_KEYS.favoritesByWorkspace(workspaceId),
          context.previousFavorites,
        );
      }
    },
    onSettled: () => {
      // Refetch to ensure we're in sync with server
      void queryClient.invalidateQueries({
        queryKey: OPPORTUNITIES_QUERY_KEYS.byWorkspace(workspaceId),
      });
    },
  });

  const { mutateAsync: toggleFavoriteAsync } = toggleFavoriteMutation;

  const handleToggleFavorite = useCallback(
    async (opportunityId: string, nextFavorite: boolean) => {
      await toggleFavoriteAsync({ opportunityId, nextFavorite });
    },
    [toggleFavoriteAsync],
  );

  return {
    opportunities: opportunities ?? null,
    isLoading,
    favoritesOnly,
    setFavoritesOnly,
    handleToggleFavorite,
  };
}
